import { useRef, useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Search, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCommandPalette } from '@/hooks/useCommandPalette'

export function CommandPaletteDialog() {
  const { isOpen, close, query, setQuery, results, selectedIndex, select } = useCommandPalette()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [isOpen])

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) close() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50 animate-in fade-in" />
        <Dialog.Content
          className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-lg bg-[#111] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-4"
          aria-label="Command palette"
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
            <Search className="h-5 w-5 text-gray-400 shrink-0" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pages, actions..."
              className="flex-1 bg-transparent text-white text-sm placeholder:text-gray-400 focus:outline-hidden"
              aria-label="Search"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 text-[10px] text-gray-400 font-mono">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[320px] overflow-y-auto py-2" role="listbox">
            {results.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                No results found
              </div>
            ) : (
              results.map((entry, i) => {
                const Icon = entry.icon
                return (
                  <button
                    key={`${entry.path}-${entry.category}-${i}`}
                    role="option"
                    aria-selected={i === selectedIndex}
                    onClick={() => select(entry)}
                    className={cn(
                      'flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors',
                      i === selectedIndex
                        ? 'bg-lime/10 text-white'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{entry.title}</p>
                      {entry.group && (
                        <p className="text-xs text-gray-400">{entry.group}</p>
                      )}
                    </div>
                    {entry.category === 'action' && (
                      <ArrowRight className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                    )}
                  </button>
                )
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-white/10 flex items-center gap-4 text-[10px] text-gray-400">
            <span><kbd className="font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="font-mono">↵</kbd> open</span>
            <span><kbd className="font-mono">esc</kbd> close</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
